import { Navigate, Outlet, createBrowserRouter, createHashRouter, useLocation } from "react-router";
import { useSession } from "../api/queries";
import { useT } from "../i18n/core";
import { LoadingRows } from "../ui/Common";
import { appMessages } from "./app.i18n";
import { Shell } from "./Shell";
import { InvitePage } from "../pages/Invite";
import { ResetPage } from "../pages/Reset";
import { ConfirmEmailPage } from "../pages/ConfirmEmail";
import { LoginPage } from "../pages/Login";
import { SignupPage } from "../pages/Signup";
import { OverviewPage } from "../pages/Overview";
import { MachinesPage } from "../pages/Machines";
import { MachineDetailPage } from "../pages/MachineDetail";
import { RestorePage } from "../pages/Restore";
import { AddMachinesPage } from "../pages/AddMachines";
import { PoliciesPage } from "../pages/Policies";
import { PolicyEditPage } from "../pages/PolicyEdit";
import { StoragePage } from "../pages/Storage";
import { AdminStoragePage } from "../pages/AdminStorage";
import { UsagePage } from "../pages/Usage";
import { ActivityPage } from "../pages/Activity";
import { ReportsPage } from "../pages/Reports";
import { OrganizationsPage } from "../pages/Organizations";
import { SettingsPage } from "../pages/Settings";
import { HelpArticlePage, HelpPage } from "../pages/Help";

function RequireSession() {
  const session = useSession();
  const location = useLocation();
  if (session.isPending) return <LoadingRows />;
  if (session.isError) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
}

function Home() {
  const session = useSession();
  const t = useT(appMessages);
  if (session.isPending) return <LoadingRows />;
  if (!session.data) return <Navigate to="/login" replace />;
  if (!session.data.homeOrgId) {
    return (
      <div className="login-form" style={{ minHeight: "100dvh" }}>
        <div className="login-card">
          <h1>{t.noOrgTitle}</h1>
          <p className="ink-2">{t.noOrgBody}</p>
        </div>
      </div>
    );
  }
  return <Navigate to={`/o/${session.data.homeOrgId}/overview`} replace />;
}

const routes = [
  { path: "/login", element: <LoginPage /> },
  { path: "/signup", element: <SignupPage /> },
  { path: "/invite", element: <InvitePage /> },
  { path: "/reset", element: <ResetPage /> },
  { path: "/confirm-email", element: <ConfirmEmailPage /> },
  {
    element: <RequireSession />,
    children: [
      { path: "/", element: <Home /> },
      {
        path: "/o/:orgId",
        element: <Shell />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <OverviewPage /> },
          { path: "machines", element: <MachinesPage /> },
          { path: "machines/:machineId", element: <MachineDetailPage /> },
          { path: "machines/:machineId/restore", element: <RestorePage /> },
          { path: "add-machines", element: <AddMachinesPage /> },
          { path: "policies", element: <PoliciesPage /> },
          { path: "policies/:policyId", element: <PolicyEditPage /> },
          { path: "storage", element: <StoragePage /> },
          { path: "platform-storage", element: <AdminStoragePage /> },
          { path: "usage", element: <UsagePage /> },
          { path: "activity", element: <ActivityPage /> },
          { path: "reports", element: <ReportsPage /> },
          { path: "organizations", element: <OrganizationsPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "help", element: <HelpPage /> },
          { path: "help/:slug", element: <HelpArticlePage /> },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
];

export const router = __ROUTER__ === "hash" ? createHashRouter(routes) : createBrowserRouter(routes);
